import { assertNever } from '@kofno/piper';
import { just, Maybe, nothing } from 'maybeasy';
import { ParticipantsResource } from '../../../../ParticipantsStore/Types';
import { TPlainTextKey } from '../../../../Translations';

export const PER_PAGE = 25;

export type SortColumn = 'name' | 'last_sign_in_at';

export type SortDirection = 'asc' | 'desc';

interface ServerDefaultSort {
  kind: 'server-default';
}

interface ColumnSort {
  kind: 'sorted';
  column: SortColumn;
  direction: SortDirection;
}

export type Sort = ServerDefaultSort | ColumnSort;

export interface Query {
  text: string;
  sort: Sort;
  page: number;
}

export interface Loading {
  kind: 'loading';
  query: Query;
}

export interface Ready {
  kind: 'ready';
  query: Query;
  resource: ParticipantsResource;
}

export interface Failed {
  kind: 'error';
  query: Query;
  message: TPlainTextKey;
}

export type State = Loading | Ready | Failed;

export const initialQuery: Query = { text: '', sort: { kind: 'server-default' }, page: 1 };

export const loading = (query: Query): Loading => ({ kind: 'loading', query });

export const ready = (query: Query, resource: ParticipantsResource): Ready => ({
  kind: 'ready',
  query,
  resource,
});

export const failed = (query: Query, message: TPlainTextKey): Failed => ({
  kind: 'error',
  query,
  message,
});

const columnSort = (column: SortColumn, direction: SortDirection): ColumnSort => ({
  kind: 'sorted',
  column,
  direction,
});

/** Names read A to Z first; last login reads most recent first. */
export const defaultDirection = (column: SortColumn): SortDirection => {
  switch (column) {
    case 'name':
      return 'asc';
    case 'last_sign_in_at':
      return 'desc';
    default:
      return assertNever(column);
  }
};

const flip = (direction: SortDirection): SortDirection => {
  switch (direction) {
    case 'asc':
      return 'desc';
    case 'desc':
      return 'asc';
    default:
      return assertNever(direction);
  }
};

/** Clicking the active column flips it; a new column starts at its default. */
export const nextSort = (sort: Sort, column: SortColumn): Sort => {
  switch (sort.kind) {
    case 'server-default':
      return columnSort(column, defaultDirection(column));
    case 'sorted':
      return sort.column === column
        ? columnSort(column, flip(sort.direction))
        : columnSort(column, defaultDirection(column));
    default:
      return assertNever(sort);
  }
};

export const sortDirectionFor = (sort: Sort, column: SortColumn): Maybe<SortDirection> => {
  switch (sort.kind) {
    case 'server-default':
      return nothing();
    case 'sorted':
      return sort.column === column ? just(sort.direction) : nothing();
    default:
      return assertNever(sort);
  }
};
